import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, Clock, Users, MessageCircle, AlertTriangle } from 'lucide-react';
import { painPointsData } from '../../data/mock';

const iconMap = {
  AlertCircle,
  Clock,
  Users,
  MessageCircle
};

const PainPointsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="py-20 md:py-28 relative overflow-hidden"
      style={{ background: 'white' }}
    >
      <div className="container">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div 
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 ${
              isVisible ? 'animate-fadeInUp' : 'opacity-0'
            }`}
            style={{ background: '#FEF3C7', color: '#92400E' }}
          >
            <AlertTriangle size={16} />
            <span className="font-semibold text-sm">The Founder Reality</span>
          </div>
          
          <h2 
            className={`heading-1 mb-4 ${isVisible ? 'animate-fadeInUp stagger-1' : 'opacity-0'}`}
            style={{ color: 'var(--text-primary)' }}
          >
            {painPointsData.title}
          </h2>
          <p 
            className={`body-large ${isVisible ? 'animate-fadeInUp stagger-2' : 'opacity-0'}`}
            style={{ color: 'var(--text-secondary)' }}
          >
            {painPointsData.subtitle}
          </p>
        </div>

        {/* Pain Points Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {painPointsData.problems.map((problem, index) => {
            const Icon = iconMap[problem.icon];
            return (
              <div
                key={index}
                className={`card group cursor-default ${
                  isVisible ? 'animate-fadeInUp' : 'opacity-0'
                }`}
                style={{ animationDelay: `${(index + 3) * 100}ms` }}
              >
                <div 
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 transition-all duration-300 group-hover:scale-110"
                  style={{ background: '#FEF3C7' }}
                >
                  <Icon size={28} style={{ color: '#92400E' }} />
                </div>
                <h3 className="heading-3 mb-3" style={{ color: 'var(--text-primary)' }}>
                  {problem.title}
                </h3>
                <p className="body-small" style={{ color: 'var(--text-light)' }}>
                  {problem.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Conclusion */}
        <div className={`text-center max-w-2xl mx-auto ${isVisible ? 'animate-fadeInUp stagger-6' : 'opacity-0'}`}>
          <p 
            className="heading-3 mb-3"
            style={{ color: 'var(--text-primary)' }}
          >
            {painPointsData.conclusion}
          </p>
          <p 
            className="body-large font-semibold"
            style={{ color: 'var(--brand-dark)' }}
          >
            {painPointsData.truth}
          </p>
        </div>
      </div>
    </section>
  );
};

export default PainPointsSection;
